'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { useDocumentEvent } from '@/hooks/useDocumentEvent';
import { useHydrateOnInteraction } from '@/hooks/useHydrateOnInteraction';
import { useWindowEvent } from '@/hooks/useWindowEvent';
import { dispatchWindowCustomEvent, getBrowserWindow } from '@/lib/utils/browser-window';

type PendingPaletteAction = 'open' | 'toggle' | null;

const CommandPalette = dynamic(() => import('./CommandPalette').then((mod) => mod.CommandPalette), {
  ssr: false,
});

const isCommandPaletteShortcut = (event: KeyboardEvent): boolean => {
  const key = event.key.toLowerCase();
  const isModule = event.metaKey || event.ctrlKey;
  return (
    (isModule && key === 'k') ||
    (isModule && event.key === '/') ||
    ((event.metaKey || event.ctrlKey) && event.shiftKey && key === 'p')
  );
};

export function LazyCommandPalette() {
  const { hasInteracted, markInteracted } = useHydrateOnInteraction({ includeKeydown: false });
  const [pendingAction, setPendingAction] = useState<PendingPaletteAction>(null);

  const handleShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (!isCommandPaletteShortcut(event)) {
        return;
      }
      event.preventDefault();
      setPendingAction('toggle');
      markInteracted();
    },
    [markInteracted]
  );

  const handleOpenEvent = useCallback(() => {
    setPendingAction('open');
    markInteracted();
  }, [markInteracted]);

  const handleToggleEvent = useCallback(() => {
    setPendingAction('toggle');
    markInteracted();
  }, [markInteracted]);

  useDocumentEvent('keydown', handleShortcut, undefined, !hasInteracted);
  useWindowEvent('sweetistics:command-palette-open', handleOpenEvent, undefined, !hasInteracted);
  useWindowEvent('sweetistics:command-palette-toggle', handleToggleEvent, undefined, !hasInteracted);

  useEffect(() => {
    if (!(hasInteracted && pendingAction)) {
      return;
    }

    const browserWindow = getBrowserWindow();
    if (!browserWindow) {
      return;
    }

    const eventName =
      pendingAction === 'open' ? 'sweetistics:command-palette-open' : 'sweetistics:command-palette-toggle';
    const frame = browserWindow.requestAnimationFrame(() => {
      dispatchWindowCustomEvent(eventName);
      setPendingAction(null);
    });

    return () => {
      browserWindow.cancelAnimationFrame(frame);
    };
  }, [hasInteracted, pendingAction]);

  if (!hasInteracted) {
    return null;
  }

  return <CommandPalette />;
}
